// Provides access to user credit balances (generation vs. cash) and a
// refreshCredits helper. Centralizing this logic keeps credit-aware components
// consistent and prevents duplicated Supabase queries.
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface CreditsContextType {
  genCredits: number;
  redeemCredits: number;
  cashCredits: number;
  loading: boolean;
  refreshCredits: () => Promise<void>;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();

  const [genCredits, setGenCredits] = useState(0);
  const [redeemCredits, setRedeemCredits] = useState(0);
  const [cashCredits, setCashCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const loadCredits = useCallback(async (userId: string | null) => {
    // If the user isn't logged in, treat balances as zero and exit early.
    if (!userId) {
      setGenCredits(0);
      setRedeemCredits(0);
      setCashCredits(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Query new user_wallet table instead of old user_credit_balances
      const { data, error } = await supabase
        .from('user_wallet')
        .select('token_balance, redeem_token_balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        setGenCredits(0);
        setRedeemCredits(0);
        setCashCredits(0);
        setLoading(false);
        return;
      }

      // token_balance is the new unified token count (replaces gen_credits)
      setGenCredits(data?.token_balance ?? 0);
      setRedeemCredits(data?.redeem_token_balance ?? 0);
      setCashCredits(0); // Cash credits no longer used in new system
      setLoading(false);
    } catch {
      setGenCredits(0);
      setRedeemCredits(0);
      setCashCredits(0);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    void loadCredits(user?.id ?? null);
  }, [authLoading, user?.id, loadCredits]);

  useEffect(() => {
    if (!user?.id) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    const channel = supabase
      .channel(`user-wallet-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_wallet',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as { token_balance?: number; redeem_token_balance?: number };
          if (typeof row.token_balance === 'number') setGenCredits(row.token_balance);
          if (typeof row.redeem_token_balance === 'number') setRedeemCredits(row.redeem_token_balance);
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [user?.id]);

  const refreshCredits = useCallback(async () => {
    if (!user?.id) {
      setGenCredits(0);
      setRedeemCredits(0);
      setCashCredits(0);
      setLoading(false);
      return;
    }
    await loadCredits(user.id);
  }, [user?.id, loadCredits]);

  const value = useMemo<CreditsContextType>(() => ({
    genCredits,
    redeemCredits,
    cashCredits,
    loading,
    refreshCredits,
  }), [genCredits, redeemCredits, cashCredits, loading, refreshCredits]);

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditsContext);
  if (!ctx) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return ctx;
}
